/**
 * Input Service
 * Handles mouse and keyboard input capture and translation
 */

import { InputCommand, InputType, MouseButton, KeyModifiers } from '../types';

export interface InputServiceConfig {
    canvas: HTMLCanvasElement;
    remoteWidth: number;
    remoteHeight: number;
    captureKeyboard: boolean;
    capturePointerLock: boolean;
}

export interface InputServiceEvents {
    onInputCommand?: (command: InputCommand) => void;
    onPointerLockChange?: (locked: boolean) => void;
}

// Virtual key code mappings (matches Windows VK codes)
const KEY_MAP: Record<string, number> = {
    'Backspace': 0x08,
    'Tab': 0x09,
    'Enter': 0x0D,
    'ShiftLeft': 0x10,
    'ShiftRight': 0x10,
    'ControlLeft': 0x11,
    'ControlRight': 0x11,
    'AltLeft': 0x12,
    'AltRight': 0x12,
    'Pause': 0x13,
    'CapsLock': 0x14,
    'Escape': 0x1B,
    'Space': 0x20,
    'PageUp': 0x21,
    'PageDown': 0x22,
    'End': 0x23,
    'Home': 0x24,
    'ArrowLeft': 0x25,
    'ArrowUp': 0x26,
    'ArrowRight': 0x27,
    'ArrowDown': 0x28,
    'PrintScreen': 0x2C,
    'Insert': 0x2D,
    'Delete': 0x2E,
    'Digit0': 0x30, 'Digit1': 0x31, 'Digit2': 0x32, 'Digit3': 0x33, 'Digit4': 0x34,
    'Digit5': 0x35, 'Digit6': 0x36, 'Digit7': 0x37, 'Digit8': 0x38, 'Digit9': 0x39,
    'KeyA': 0x41, 'KeyB': 0x42, 'KeyC': 0x43, 'KeyD': 0x44, 'KeyE': 0x45,
    'KeyF': 0x46, 'KeyG': 0x47, 'KeyH': 0x48, 'KeyI': 0x49, 'KeyJ': 0x4A,
    'KeyK': 0x4B, 'KeyL': 0x4C, 'KeyM': 0x4D, 'KeyN': 0x4E, 'KeyO': 0x4F,
    'KeyP': 0x50, 'KeyQ': 0x51, 'KeyR': 0x52, 'KeyS': 0x53, 'KeyT': 0x54,
    'KeyU': 0x55, 'KeyV': 0x56, 'KeyW': 0x57, 'KeyX': 0x58, 'KeyY': 0x59, 'KeyZ': 0x5A,
    'MetaLeft': 0x5B,
    'MetaRight': 0x5C,
    'ContextMenu': 0x5D,
    'Numpad0': 0x60, 'Numpad1': 0x61, 'Numpad2': 0x62, 'Numpad3': 0x63, 'Numpad4': 0x64,
    'Numpad5': 0x65, 'Numpad6': 0x66, 'Numpad7': 0x67, 'Numpad8': 0x68, 'Numpad9': 0x69,
    'NumpadMultiply': 0x6A,
    'NumpadAdd': 0x6B,
    'NumpadSubtract': 0x6D,
    'NumpadDecimal': 0x6E,
    'NumpadDivide': 0x6F,
    'F1': 0x70, 'F2': 0x71, 'F3': 0x72, 'F4': 0x73, 'F5': 0x74, 'F6': 0x75,
    'F7': 0x76, 'F8': 0x77, 'F9': 0x78, 'F10': 0x79, 'F11': 0x7A, 'F12': 0x7B,
    'NumLock': 0x90,
    'ScrollLock': 0x91,
    'Semicolon': 0xBA,
    'Equal': 0xBB,
    'Comma': 0xBC,
    'Minus': 0xBD,
    'Period': 0xBE,
    'Slash': 0xBF,
    'Backquote': 0xC0,
    'BracketLeft': 0xDB,
    'Backslash': 0xDC,
    'BracketRight': 0xDD,
    'Quote': 0xDE
};

export class InputService {
    private canvas: HTMLCanvasElement;
    private config: InputServiceConfig;
    private events: InputServiceEvents;
    private isCapturing = false;
    private isPointerLocked = false;

    // Throttling
    private lastMouseMoveTime = 0;
    private mouseMoveThrottleMs = 16; // ~60fps max
    private pendingMouseMove: InputCommand | null = null;
    private mouseMoveAnimationFrame: number | null = null;

    // Key state tracking (for preventing repeat events)
    private pressedKeys = new Set<string>();

    constructor(config: InputServiceConfig, events: InputServiceEvents = {}) {
        this.canvas = config.canvas;
        this.config = config;
        this.events = events;
    }

    /**
     * Start capturing input
     */
    startCapture(): void {
        if (this.isCapturing) return;

        this.setupMouseHandlers();
        this.setupKeyboardHandlers();
        this.setupPointerLockHandlers();

        // Auto-focus canvas so keyboard works immediately
        this.canvas.focus();

        this.isCapturing = true;
        console.log('Input capture started');
    }

    /**
     * Stop capturing input
     */
    stopCapture(): void {
        if (!this.isCapturing) return;

        this.removeMouseHandlers();
        this.removeKeyboardHandlers();
        this.releasePointerLock();

        this.isCapturing = false;
        this.pressedKeys.clear();
        console.log('Input capture stopped');
    }

    /**
     * Update remote screen dimensions
     */
    updateRemoteDimensions(width: number, height: number): void {
        this.config.remoteWidth = width;
        this.config.remoteHeight = height;
    }

    /**
     * Request pointer lock (hides cursor, captures all mouse movement)
     */
    requestPointerLock(): void {
        if (this.config.capturePointerLock) {
            this.canvas.requestPointerLock();
        }
    }

    /**
     * Release pointer lock
     */
    releasePointerLock(): void {
        if (document.pointerLockElement === this.canvas) {
            document.exitPointerLock();
        }
    }

    /**
     * Check if pointer is locked
     */
    get pointerLocked(): boolean {
        return this.isPointerLocked;
    }

    // =========================================================================
    // Mouse Handlers
    // =========================================================================

    private setupMouseHandlers(): void {
        this.canvas.addEventListener('mousedown', this.handleMouseDown);
        this.canvas.addEventListener('mouseup', this.handleMouseUp);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('wheel', this.handleWheel, { passive: false });
        this.canvas.addEventListener('contextmenu', this.handleContextMenu);

        // Prevent default drag behavior
        this.canvas.addEventListener('dragstart', this.preventDefault);
    }

    private removeMouseHandlers(): void {
        this.canvas.removeEventListener('mousedown', this.handleMouseDown);
        this.canvas.removeEventListener('mouseup', this.handleMouseUp);
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
        this.canvas.removeEventListener('wheel', this.handleWheel);
        this.canvas.removeEventListener('contextmenu', this.handleContextMenu);
        this.canvas.removeEventListener('dragstart', this.preventDefault);
    }

    private handleMouseDown = (event: MouseEvent): void => {
        // Focus canvas so keyboard events are captured
        this.canvas.focus();

        const { x, y } = this.translateCoordinates(event);
        const button = this.translateMouseButton(event.button);

        this.emitCommand({
            type: InputType.MouseDown,
            x,
            y,
            button,
            modifiers: this.getModifiers(event),
            timestamp: Date.now()
        });

        event.preventDefault();
    };

    private handleMouseUp = (event: MouseEvent): void => {
        const { x, y } = this.translateCoordinates(event);
        const button = this.translateMouseButton(event.button);

        this.emitCommand({
            type: InputType.MouseUp,
            x,
            y,
            button,
            modifiers: this.getModifiers(event),
            timestamp: Date.now()
        });

        event.preventDefault();
    };

    private handleMouseMove = (event: MouseEvent): void => {
        const now = Date.now();
        const { x, y } = this.translateCoordinates(event);

        // Create command
        const command: InputCommand = {
            type: InputType.MouseMove,
            x,
            y,
            modifiers: this.getModifiers(event),
            timestamp: now
        };

        // Throttle mouse move events
        if (now - this.lastMouseMoveTime < this.mouseMoveThrottleMs) {
            // Store pending move, will be sent on next animation frame
            this.pendingMouseMove = command;

            if (this.mouseMoveAnimationFrame === null) {
                this.mouseMoveAnimationFrame = requestAnimationFrame(() => {
                    if (this.pendingMouseMove) {
                        this.emitCommand(this.pendingMouseMove);
                        this.pendingMouseMove = null;
                    }
                    this.mouseMoveAnimationFrame = null;
                });
            }
            return;
        }

        this.lastMouseMoveTime = now;
        this.emitCommand(command);
    };

    private handleWheel = (event: WheelEvent): void => {
        const { x, y } = this.translateCoordinates(event);

        // Normalize wheel delta
        let wheelDelta = 0;
        if (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL) {
            wheelDelta = Math.sign(event.deltaY) * -120;
        } else if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
            wheelDelta = event.deltaY * -120;
        } else {
            wheelDelta = event.deltaY * -120 * 3;
        }

        this.emitCommand({
            type: InputType.MouseWheel,
            x,
            y,
            wheelDelta,
            modifiers: this.getModifiers(event),
            timestamp: Date.now()
        });

        event.preventDefault();
    };

    private handleContextMenu = (event: Event): void => {
        event.preventDefault();
    };

    // =========================================================================
    // Keyboard Handlers
    // =========================================================================

    private setupKeyboardHandlers(): void {
        if (!this.config.captureKeyboard) return;

        // Use window for keyboard events to capture even when canvas doesn't have focus
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);

        // Make canvas focusable
        this.canvas.tabIndex = 0;
    }

    private removeKeyboardHandlers(): void {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    private handleKeyDown = (event: KeyboardEvent): void => {
        // Only capture if canvas is focused or pointer is locked
        if (!this.shouldCaptureKeyboard()) return;

        // Prevent key repeat events
        if (this.pressedKeys.has(event.code)) {
            event.preventDefault();
            return;
        }

        this.pressedKeys.add(event.code);

        const keyCode = this.translateKeyCode(event.code);

        // Handle special text input for non-ASCII characters
        if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
            // Send as text input for proper Unicode support
            this.emitCommand({
                type: InputType.TextInput,
                text: event.key,
                modifiers: this.getModifiers(event),
                timestamp: Date.now()
            });
        } else {
            this.emitCommand({
                type: InputType.KeyDown,
                keyCode,
                scanCode: event.keyCode,
                modifiers: this.getModifiers(event),
                timestamp: Date.now()
            });
        }

        // Prevent browser shortcuts (except for some essential ones)
        if (!this.shouldAllowBrowserShortcut(event)) {
            event.preventDefault();
        }
    };

    private handleKeyUp = (event: KeyboardEvent): void => {
        if (!this.shouldCaptureKeyboard()) return;

        this.pressedKeys.delete(event.code);

        const keyCode = this.translateKeyCode(event.code);

        this.emitCommand({
            type: InputType.KeyUp,
            keyCode,
            scanCode: event.keyCode,
            modifiers: this.getModifiers(event),
            timestamp: Date.now()
        });

        event.preventDefault();
    };

    private shouldCaptureKeyboard(): boolean {
        return this.isPointerLocked || document.activeElement === this.canvas;
    }

    private shouldAllowBrowserShortcut(event: KeyboardEvent): boolean {
        // Allow browser refresh, dev tools, etc.
        if (event.key === 'F5' || event.key === 'F12') return true;
        if ((event.ctrlKey || event.metaKey) && event.key === 'r') return true;
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'I') return true;

        return false;
    }

    // =========================================================================
    // Pointer Lock Handlers
    // =========================================================================

    private setupPointerLockHandlers(): void {
        document.addEventListener('pointerlockchange', this.handlePointerLockChange);
        document.addEventListener('pointerlockerror', this.handlePointerLockError);
    }

    private handlePointerLockChange = (): void => {
        this.isPointerLocked = document.pointerLockElement === this.canvas;
        this.events.onPointerLockChange?.(this.isPointerLocked);
        console.log('Pointer lock:', this.isPointerLocked);
    };

    private handlePointerLockError = (): void => {
        console.error('Pointer lock error');
    };

    // =========================================================================
    // Utility Methods
    // =========================================================================

    private translateCoordinates(event: MouseEvent): { x: number; y: number } {
        const rect = this.canvas.getBoundingClientRect();

        // Calculate position relative to canvas
        let canvasX: number;
        let canvasY: number;

        if (this.isPointerLocked) {
            // Use movement data for pointer lock mode
            canvasX = (event as MouseEvent).movementX || 0;
            canvasY = (event as MouseEvent).movementY || 0;
        } else {
            canvasX = event.clientX - rect.left;
            canvasY = event.clientY - rect.top;
        }

        // Scale to remote screen dimensions
        const scaleX = this.config.remoteWidth / this.canvas.clientWidth;
        const scaleY = this.config.remoteHeight / this.canvas.clientHeight;

        const x = Math.round(canvasX * scaleX);
        const y = Math.round(canvasY * scaleY);

        // Clamp to remote screen bounds
        return {
            x: Math.max(0, Math.min(this.config.remoteWidth - 1, x)),
            y: Math.max(0, Math.min(this.config.remoteHeight - 1, y))
        };
    }

    private translateMouseButton(button: number): MouseButton {
        switch (button) {
            case 0: return MouseButton.Left;
            case 1: return MouseButton.Middle;
            case 2: return MouseButton.Right;
            case 3: return MouseButton.X1;
            case 4: return MouseButton.X2;
            default: return MouseButton.Left;
        }
    }

    private translateKeyCode(code: string): number {
        return KEY_MAP[code] || 0;
    }

    private getModifiers(event: MouseEvent | KeyboardEvent): KeyModifiers {
        return {
            ctrl: event.ctrlKey,
            alt: event.altKey,
            shift: event.shiftKey,
            meta: event.metaKey
        };
    }

    private emitCommand(command: InputCommand): void {
        this.events.onInputCommand?.(command);
    }

    private preventDefault = (event: Event): void => {
        event.preventDefault();
    };
}
